import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ResponsiveNotePhoto, ResponsiveNotePhotoLabel } from "@rebuild/shared/models";
import { getRebuildAPI } from "@rebuild/shared/api";
import { RESPONSIVE_NOTE_PHOTO_KEYS } from "@/hooks/queries/responsive-note-photos/queryKeys";
import { mergePhotoLabels, removePhotoLabels } from "@/components/estimation-details/details-section/Photos/photoLabels.utils";

const { estimatesControllerApplyResponsiveNotePhotoLabels } = getRebuildAPI();

interface ApplyResponsiveNotePhotoLabelsMutationParams {
  estimateId: string;
  photos: { noteId: string; photoId: string }[];
  labels: ResponsiveNotePhotoLabel[];
  mode: "add" | "replace" | "remove";
}

export function useApplyResponsiveNotePhotoLabelsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ApplyResponsiveNotePhotoLabelsMutationParams) => {
      const response = await estimatesControllerApplyResponsiveNotePhotoLabels(params.estimateId, {
        photos: params.photos,
        labels: params.labels,
        mode: params.mode,
      });

      if (!response.success) {
        throw new Error("Failed to apply labels");
      }

      return response.data;
    },
    // The toolbar clears the selection before awaiting, so patch the cache up front or the
    // tiles visibly flash back to unlabelled until the refetch lands.
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: RESPONSIVE_NOTE_PHOTO_KEYS.estimate(variables.estimateId),
      });

      const previousPhotos = queryClient.getQueryData<ResponsiveNotePhoto[]>(
        RESPONSIVE_NOTE_PHOTO_KEYS.estimate(variables.estimateId),
      );

      if (previousPhotos) {
        const photoKeys = new Set(variables.photos.map((photo) => `${photo.noteId}:${photo.photoId}`));
        const nextPhotos = previousPhotos.map((photo) => {
          if (!photoKeys.has(`${photo.noteId}:${photo.id}`)) {
            return photo;
          }

          return {
            ...photo,
            // Every branch goes through the shared helpers so the optimistic state matches the
            // server's normalization; otherwise tiles briefly show labels the refetch then changes.
            labels:
              variables.mode === "replace"
                ? mergePhotoLabels([], variables.labels)
                : variables.mode === "remove"
                  ? removePhotoLabels(photo.labels, variables.labels)
                  : mergePhotoLabels(photo.labels, variables.labels),
          };
        });
        queryClient.setQueryData(RESPONSIVE_NOTE_PHOTO_KEYS.estimate(variables.estimateId), nextPhotos);
      }

      return { previousPhotos };
    },
    onError: (_error, variables, context) => {
      if (context?.previousPhotos) {
        queryClient.setQueryData(RESPONSIVE_NOTE_PHOTO_KEYS.estimate(variables.estimateId), context.previousPhotos);
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: RESPONSIVE_NOTE_PHOTO_KEYS.estimate(variables.estimateId),
      });
      // A free-form tag becomes part of the estimate's vocabulary the moment it lands on a photo,
      // so the picker's own list has to refetch rather than wait to go stale.
      queryClient.invalidateQueries({
        queryKey: RESPONSIVE_NOTE_PHOTO_KEYS.estimateLabels(variables.estimateId),
      });
    },
  });
}
